/**
 * A RING — one part against one whole, with the figure in the middle.
 *
 * ── WHY A RING AND NOT A BAR ──
 *
 * Almost never. A ring is worse than a bar for reading a value and worse than a
 * bar for comparing two. It earns its place in exactly one case: a single
 * PROPORTION OF A KNOWN WHOLE, where the whole is the point — "how full is the
 * shelf", "how much of the window breached the threshold". The shape of the gap
 * is the message, and it is legible at a glance from across a desk, which is
 * where this product's readers actually are.
 *
 * It is used twice on this screen and nowhere else. If a third case appears,
 * check first that the whole is really known.
 *
 * ── AND WHY IT IS NOT USED FOR OCCUPANCY ──
 *
 * The turnover panel deliberately prints NO single occupancy percentage, because
 * "vacant" and "nobody has recorded an occupancy" are different facts and only
 * one of them is a fact. A ring is precisely the control that would force them
 * into one number. That refusal stands, and this component is not the way around
 * it.
 *
 * ── THE THREE THINGS IT REFUSES TO DO ──
 *
 *   · No ring at all when the whole is zero or unknown. A ring of nothing reads
 *     as "0%" — a measurement — when the truth is "there is nothing to take a
 *     share of".
 *   · Never over 100%. A part larger than its whole is a counting error
 *     upstream, and drawing 140% of a ring hides it. The ring caps and the
 *     caption says the raw pair.
 *   · No semantic colour by default. More parcels on the shelf is not bad news
 *     and fewer is not good; the tone is passed in by the panel that knows what
 *     the number means.
 */
import React from 'react';
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { RingBox, RingCentre, RingFigure, RingNote, RingUnit } from './chart.styled';

export interface RingChartProps {
  /** The part. */
  value: number;
  /** The whole. Null or zero renders the absence, never a zero ring. */
  total: number | null;
  /** Big text in the middle. Defaults to the rounded percentage. */
  centre?: string;
  unit?: string;
  /** One line under the figure — the raw pair, so the ring is never the only
   *  statement of it. */
  note?: React.ReactNode;
  tone?: string;
  /** For the accessible name; a ring with no words is invisible to a reader who
   *  cannot see it. */
  label: string;
  testId?: string;
}

export const RingChart: React.FC<RingChartProps> = ({
  value,
  total,
  centre,
  unit = '%',
  note,
  tone = dataViz.series[0],
  label,
  testId,
}) => {
  if (!total || total <= 0) {
    return (
      <RingBox data-testid={testId ? `${testId}-absent` : undefined}>
        <RingCentre>
          <RingFigure>—</RingFigure>
        </RingCentre>
        <RingNote>
          {label}: nothing was recorded in this period, so there is no share to take.
        </RingNote>
      </RingBox>
    );
  }

  const raw = (value / total) * 100;
  const pct = Math.round(raw * 10) / 10;
  // See the header: a part larger than its whole is an upstream counting error,
  // and a ring drawn past full would hide it.
  const drawn = Math.max(0, Math.min(100, raw));

  return (
    <RingBox
      data-testid={testId}
      role="img"
      aria-label={`${label}: ${value.toLocaleString()} of ${total.toLocaleString()}, ${pct}%`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart
          innerRadius="72%"
          outerRadius="100%"
          startAngle={90}
          endAngle={-270}
          data={[{ name: label, value: drawn, fill: tone }]}
        >
          {/* THE TRACK. Without an explicit domain the single bar fills the ring
              whatever its value, which would draw 3% as a complete circle. */}
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar
            dataKey="value"
            background={{ fill: palette.surfaceMuted }}
            cornerRadius={99}
            isAnimationActive={false}
          />
        </RadialBarChart>
      </ResponsiveContainer>

      <RingCentre aria-hidden="true">
        <RingFigure>{centre ?? pct.toLocaleString()}</RingFigure>
        {centre ? null : <RingUnit>{unit}</RingUnit>}
      </RingCentre>

      {note ? <RingNote>{note}</RingNote> : null}
    </RingBox>
  );
};
